import React, { useMemo, useRef, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from '../ui/accordion';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import {
  AlertCircle, AlertTriangle, Thermometer, BatteryCharging, Play, Loader2, RotateCcw, CarFront, Route, Snowflake, Link2,
} from 'lucide-react';
import {
  PROFILE_OPTIONS, DRIVE_CYCLE_OPTIONS, CHEMISTRY_OPTIONS, STRATEGY_OPTIONS, PACK_FIELD_SPECS, AMBIENT_SPEC,
  START_TEMP_SPEC, REPEATS_SPEC, CHARGE_FIELD_SPECS, COOLING_FIELD_SPECS, THERMAL_PRESETS, DEFAULT_PRESET_ID,
  DEFAULT_CONDITIONS, DEFAULT_PROFILE, DEFAULT_START_SOC, MAX_REPEATS, findPreset, isDriveProfile, isChargeProfile,
  strategyParamsForPack, validateField, validateCrossFields,
} from '../../lib/batteryThermalTools';
import { simulationApi } from '../../lib/simulationApi';
import { BatteryThermalSummaryPanel, StrategyComparisonTable } from './simulator/BatteryThermalSummaryPanel';
import {
  TemperatureChart, PowerDerateChart, HeatBalanceChart, SocCurrentChart, StrategyOverlayChart,
} from './simulator/BatteryThermalCharts';

const FieldInput = ({ spec, raw, error, onChange, testPrefix, hint }) => (
  <div className="space-y-1">
    <Label htmlFor={`${testPrefix}-${spec.key}`} className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 leading-tight">
      {spec.label} <span className="normal-case text-muted-foreground/70">({spec.unit})</span>
    </Label>
    <Input
      id={`${testPrefix}-${spec.key}`} type="number" step="any" value={raw ?? ''}
      onChange={(e) => onChange(spec.key, e.target.value)} className="font-mono h-8 text-sm"
      aria-invalid={!!error} aria-describedby={error ? `${testPrefix}-${spec.key}-error` : undefined}
      data-testid={`${testPrefix}-${spec.key}-input`}
    />
    {hint && !error && <p className="text-[11px] text-muted-foreground leading-tight">{hint}</p>}
    {error && (
      <p id={`${testPrefix}-${spec.key}-error`} className="flex items-center gap-1 text-xs text-destructive" data-testid={`${testPrefix}-${spec.key}-error`}>
        <AlertCircle className="h-3 w-3 shrink-0" /> {error}
      </p>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, testId }) => (
  <div className="space-y-1">
    <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight">{label}</Label>
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-8 text-sm" data-testid={testId}><SelectValue /></SelectTrigger>
      <SelectContent>
        {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

const Section = ({ value, icon: Icon, title, children }) => (
  <AccordionItem value={value}>
    <AccordionTrigger className="font-chivo text-xs font-bold uppercase tracking-wider text-muted-foreground py-2">
      <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {title}</span>
    </AccordionTrigger>
    <AccordionContent className="space-y-3 pb-3 pt-0">{children}</AccordionContent>
  </AccordionItem>
);

const GRID = 'grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-3';
// Start SoC a profile switch pre-fills (a fast charge needs room below the target).
const PROFILE_START_SOC = { drive: '90', charge: '10', drive_then_charge: '70' };

function validateSpecs(raw, specs) {
  const errors = {};
  const values = {};
  for (const spec of specs) {
    const err = validateField(spec, raw[spec.key]);
    if (err) errors[spec.key] = err;
    else values[spec.key] = Number(raw[spec.key]);
  }
  return { errors, values };
}

const BatteryThermalSimulator = () => {
  const initial = findPreset(DEFAULT_PRESET_ID);
  const [presetId, setPresetId] = useState(DEFAULT_PRESET_ID);
  const [chemistry, setChemistry] = useState(initial.chemistry);
  const [packRaw, setPackRaw] = useState({
    pack_energy_kwh: String(initial.pack_energy_kwh), pack_nominal_voltage_v: String(initial.pack_nominal_voltage_v),
    start_soc_pct: PROFILE_START_SOC[DEFAULT_PROFILE.profile] || DEFAULT_START_SOC,
  });
  const [condRaw, setCondRaw] = useState(DEFAULT_CONDITIONS);
  // Start Pack Temperature follows Ambient until edited directly (Charging Simulator convention).
  const [tempLinked, setTempLinked] = useState(true);
  const [profileRaw, setProfileRaw] = useState({ ...DEFAULT_PROFILE, vehicle_max_charge_kw: String(initial.vehicle_max_charge_kw) });
  const [strategy, setStrategy] = useState(initial.strategy);
  const [coolingRaw, setCoolingRaw] = useState(() => strategyParamsForPack(initial.strategy, initial.pack_energy_kwh));
  const [coolingEdited, setCoolingEdited] = useState(false);
  const preset = findPreset(presetId);

  const profile = profileRaw.profile;
  const drives = isDriveProfile(profile);
  const charges = isChargeProfile(profile);

  const loadPreset = (id) => {
    const p = findPreset(id);
    if (!p) return;
    setPresetId(id);
    setChemistry(p.chemistry);
    setPackRaw((prev) => ({ ...prev, pack_energy_kwh: String(p.pack_energy_kwh), pack_nominal_voltage_v: String(p.pack_nominal_voltage_v) }));
    setProfileRaw((prev) => ({ ...prev, vehicle_max_charge_kw: String(p.vehicle_max_charge_kw) }));
    setStrategy(p.strategy);
    setCoolingRaw(strategyParamsForPack(p.strategy, p.pack_energy_kwh));
    setCoolingEdited(false);
  };

  const setPackField = (key, value) => {
    setPackRaw((prev) => ({ ...prev, [key]: value }));
    // Conductances/thermal masses scale with pack energy — follow it until the user edits them.
    if (key === 'pack_energy_kwh' && !coolingEdited && Number(value) > 0) setCoolingRaw(strategyParamsForPack(strategy, value));
  };
  const setCondField = (key, value) => {
    if (key === 'start_temp_c') {
      setTempLinked(false);
      setCondRaw((prev) => ({ ...prev, start_temp_c: value }));
    } else {
      setCondRaw((prev) => ({ ...prev, ambient_temp_c: value, ...(tempLinked ? { start_temp_c: value } : {}) }));
    }
  };
  const relinkTemp = () => {
    setTempLinked(true);
    setCondRaw((prev) => ({ ...prev, start_temp_c: prev.ambient_temp_c }));
  };
  const setProfileField = (key, value) => {
    setProfileRaw((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'drive_cycle' && Number(prev.repeats) > MAX_REPEATS[value]) next.repeats = String(MAX_REPEATS[value]);
      return next;
    });
    if (key === 'profile') setPackRaw((prev) => ({ ...prev, start_soc_pct: PROFILE_START_SOC[value] }));
  };
  const changeStrategy = (value) => {
    setStrategy(value);
    setCoolingRaw(strategyParamsForPack(value, packRaw.pack_energy_kwh));
    setCoolingEdited(false);
  };
  const setCoolingField = (key, value) => {
    setCoolingEdited(true);
    setCoolingRaw((prev) => ({ ...prev, [key]: value }));
  };
  const resetCooling = () => {
    setCoolingRaw(strategyParamsForPack(strategy, packRaw.pack_energy_kwh));
    setCoolingEdited(false);
  };

  const repeatsSpec = REPEATS_SPEC(profileRaw.drive_cycle);
  const validation = useMemo(() => {
    const pack = validateSpecs(packRaw, PACK_FIELD_SPECS);
    const cond = validateSpecs(condRaw, [AMBIENT_SPEC, START_TEMP_SPEC]);
    const prof = validateSpecs(profileRaw, [...(drives ? [repeatsSpec] : []), ...(charges ? CHARGE_FIELD_SPECS : [])]);
    const cool = validateSpecs(coolingRaw, COOLING_FIELD_SPECS);
    const cross = validateCrossFields(profile, { ...pack.values, ...prof.values, min_soc_pct: preset?.min_soc_pct ?? 5 });
    if (cross.target_soc_pct) prof.errors.target_soc_pct = cross.target_soc_pct;
    if (cross.start_soc_pct) pack.errors.start_soc_pct = cross.start_soc_pct;
    return { pack, cond, prof, cool };
  }, [packRaw, condRaw, profileRaw, coolingRaw, drives, charges, profile, repeatsSpec, preset]);
  const hasErrors = Object.values(validation).some((g) => Object.keys(g.errors).length > 0);

  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);
  const [result, setResult] = useState(null);
  const [resultProfile, setResultProfile] = useState(null);
  // Bumped per run: re-mounts the result charts so zoom state never carries over to new data.
  const [runCount, setRunCount] = useState(0);
  const snapshotRef = useRef(null);
  const snapshot = () => JSON.stringify({ presetId, chemistry, packRaw, condRaw, profileRaw, strategy, coolingRaw });
  const stale = !!result && snapshotRef.current !== null && snapshotRef.current !== snapshot();

  const runSimulation = async () => {
    setRunning(true);
    setRunError(null);
    const v = validation;
    try {
      const response = await simulationApi.runBatteryThermal({
        profile,
        vehicle: drives ? preset.vehicle : null,
        chemistry,
        pack_energy_kwh: v.pack.values.pack_energy_kwh,
        pack_nominal_voltage_v: v.pack.values.pack_nominal_voltage_v,
        aux_power_w: preset.aux_power_w,
        ambient_temp_c: v.cond.values.ambient_temp_c,
        start_temp_c: v.cond.values.start_temp_c,
        start_soc_pct: v.pack.values.start_soc_pct,
        min_soc_pct: preset.min_soc_pct,
        cooling: { strategy, ...v.cool.values },
        drive_cycle: profileRaw.drive_cycle,
        repeats: drives ? v.prof.values.repeats : 1,
        charger_power_kw: charges ? v.prof.values.charger_power_kw : 150,
        vehicle_max_charge_kw: charges ? v.prof.values.vehicle_max_charge_kw : null,
        target_soc_pct: charges ? v.prof.values.target_soc_pct : 80,
      });
      setResult(response);
      setResultProfile(profile);
      setRunCount((n) => n + 1);
      snapshotRef.current = snapshot();
    } catch (e) {
      setRunError(e.message);
    }
    setRunning(false);
  };

  const errs = validation;
  return (
    <div className="space-y-6">
      <Card className="technical-card">
        <CardHeader className="p-4 pb-2">
          <CardTitle className="font-chivo uppercase tracking-tight flex items-center gap-2 text-base">
            <Thermometer className="h-5 w-5 text-autoxium-blue" />
            Battery Pack Thermal Simulator
          </CardTitle>
          <CardDescription className="text-xs">
            Pick a vehicle, a cooling strategy and a profile — a drive cycle, a DC fast charge, or a drive
            followed by a fast charge — then submit to see pack temperatures, BMS derating, and how every
            cooling strategy compares on the same run.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-4 pt-0 space-y-1">
          <div className="rounded-md border border-border/60 bg-muted/30 p-3 mb-2 space-y-2" data-testid="thermal-preset-panel">
            <div className="space-y-1">
              <Label className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground leading-tight flex items-center gap-1.5">
                <CarFront className="h-3.5 w-3.5" /> Vehicle Preset
              </Label>
              <Select value={presetId} onValueChange={loadPreset}>
                <SelectTrigger className="h-8 text-sm" data-testid="thermal-preset-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {THERMAL_PRESETS.map((p) => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {preset && (
              <p className="text-xs text-muted-foreground" data-testid="thermal-preset-note">
                Road load, motor ({preset.vehicle.peak_power_kw} kW / {preset.vehicle.peak_torque_nm} Nm) and
                auxiliary load come from the Regen Braking Simulator's preset for this car; pack, cooling
                and charge fields below are pre-filled and editable.
              </p>
            )}
          </div>

          <Accordion type="multiple" defaultValue={['profile', 'pack', 'cooling']}>
            <Section value="profile" icon={Route} title="Profile & Conditions">
              <div className={GRID}>
                <SelectField label="Profile" value={profile} onChange={(v) => setProfileField('profile', v)} options={PROFILE_OPTIONS} testId="thermal-profile-select" />
                <FieldInput spec={AMBIENT_SPEC} raw={condRaw.ambient_temp_c} error={errs.cond.errors.ambient_temp_c} onChange={setCondField} testPrefix="thermal" />
                <div className="space-y-1">
                  <FieldInput spec={START_TEMP_SPEC} raw={condRaw.start_temp_c} error={errs.cond.errors.start_temp_c} onChange={setCondField} testPrefix="thermal"
                    hint={tempLinked ? 'Follows Ambient until you edit it.' : null} />
                  {!tempLinked && (
                    <button type="button" onClick={relinkTemp} className="flex items-center gap-1 text-[11px] font-mono uppercase tracking-wider text-autoxium-blue hover:underline" data-testid="thermal-relink-temp">
                      <Link2 className="h-3 w-3" /> Link to ambient
                    </button>
                  )}
                </div>
                {drives && (
                  <SelectField label="Drive Cycle" value={profileRaw.drive_cycle} onChange={(v) => setProfileField('drive_cycle', v)} options={DRIVE_CYCLE_OPTIONS} testId="thermal-cycle-select" />
                )}
                {drives && (
                  <FieldInput spec={repeatsSpec} raw={profileRaw.repeats} error={errs.prof.errors.repeats} onChange={setProfileField} testPrefix="thermal"
                    hint={`Back-to-back, up to ${MAX_REPEATS[profileRaw.drive_cycle]}×.`} />
                )}
                {charges && CHARGE_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={profileRaw[spec.key]} error={errs.prof.errors[spec.key]} onChange={setProfileField} testPrefix="thermal" />
                ))}
              </div>
              {profile === 'drive_then_charge' && (
                <p className="text-xs text-muted-foreground">
                  The fast charge starts the moment the drive ends, with the pack, coolant and SoC exactly
                  as the drive left them — the realistic hot-pack case (motorway run, then DC charging).
                </p>
              )}
            </Section>

            <Section value="pack" icon={BatteryCharging} title="Battery Pack">
              <div className={GRID}>
                <SelectField label="Cell Chemistry" value={chemistry} onChange={setChemistry} options={CHEMISTRY_OPTIONS} testId="thermal-chemistry-select" />
                {PACK_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={packRaw[spec.key]} error={errs.pack.errors[spec.key]} onChange={setPackField} testPrefix="thermal" />
                ))}
              </div>
            </Section>

            <Section value="cooling" icon={Snowflake} title="Cooling Strategy">
              <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                <div className="flex-1 min-w-0">
                  <SelectField label="Strategy" value={strategy} onChange={changeStrategy} options={STRATEGY_OPTIONS} testId="thermal-strategy-select" />
                </div>
                {coolingEdited && (
                  <Button variant="outline" size="sm" className="h-8 shrink-0" onClick={resetCooling} data-testid="thermal-cooling-reset">
                    <RotateCcw className="h-3.5 w-3.5" /> Reset to preset
                  </Button>
                )}
              </div>
              <div className={GRID}>
                {COOLING_FIELD_SPECS.map((spec) => (
                  <FieldInput key={spec.key} spec={spec} raw={coolingRaw[spec.key]} error={errs.cool.errors[spec.key]} onChange={setCoolingField} testPrefix="thermal-cooling" />
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Preset values are for this pack size (conductances and thermal masses scale with pack
                energy). The chiller and radiator switch on at 30 °C and off at 27 °C; the heater on at
                10 °C and off at 13 °C. Sources and the calibration are in the User Guide.
              </p>
            </Section>
          </Accordion>

          <div className="pt-3">
            <Button onClick={runSimulation} disabled={hasErrors || running} className="w-full" data-testid="run-thermal-simulation-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Submit &amp; Run Simulation
            </Button>
            {runError && (
              <div className="flex items-center gap-2 text-sm text-destructive mt-3" data-testid="thermal-run-error">
                <AlertCircle className="h-4 w-4 shrink-0" /> {runError}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {result && (
        <div key={runCount} className={`space-y-6 transition-opacity ${stale ? 'opacity-50 pointer-events-none' : ''}`} data-testid="thermal-results">
          {stale && (
            <Alert data-testid="thermal-stale-banner" className="pointer-events-auto">
              <RotateCcw className="h-4 w-4" />
              <AlertDescription>Inputs have changed — submit again to update these results.</AlertDescription>
            </Alert>
          )}
          {result.warnings?.length > 0 && (
            <Alert data-testid="thermal-warnings" className="border-amber-500/50">
              <AlertTriangle className="h-4 w-4 text-amber-500" />
              <AlertDescription>
                <p className="font-semibold mb-1">The simulation ran, but check these points:</p>
                <ul className="list-disc pl-5 space-y-1 text-sm">
                  {result.warnings.map((w) => <li key={w}>{w}</li>)}
                </ul>
              </AlertDescription>
            </Alert>
          )}
          <BatteryThermalSummaryPanel summary={result.summary} limits={result.limits} limiterShares={result.limiter_shares} profile={resultProfile} />
          <TemperatureChart series={result.series} limits={result.limits} />
          <PowerDerateChart series={result.series} profile={resultProfile} />
          <HeatBalanceChart series={result.series} />
          <SocCurrentChart series={result.series} />
          <StrategyComparisonTable comparison={result.comparison} limits={result.limits} profile={resultProfile} />
          <StrategyOverlayChart comparison={result.comparison} limits={result.limits} />
        </div>
      )}

      {!result && (
        <Card className="technical-card">
          <CardContent className="py-10 text-center text-sm text-muted-foreground" data-testid="thermal-empty-state">
            Fill in the inputs above and submit to see results and charts.
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default BatteryThermalSimulator;
